// Libraries
import { VictoryLabel } from 'victory';
import { useBreakpointValue } from '@chakra-ui/react';
// Utils
import { tickLabelStylesMap, maxCharsPerLineMap } from './TickLabel.breakpoints';
import { splitStringIntoArrayLines } from '@helpers/index';
// Types
import { TickLabelProps } from './TickLabel.types';
import { Fragment } from 'react';

/**
 * TickLabel component renders multi-line labels for Victory charts.
 * It takes a text string as input and splits it into multiple lines based on the maximum
 * characters per line.
 * Each line is rendered as a separate VictoryLabel component.
 *
 * @component
 * @param {TickLabelProps} props - Props for the TickLabel component.
 * @param {number} props.x - Position in x for the label.
 * @param {number} props.y -  Position in y for the label.
 * @param {string} props.text - The text to be shown.
 * @returns {ReactElement} The rendered lines as a component.
 */

export const TickLabel = ({ x, y = 0, text }: TickLabelProps) => {
  const style = useBreakpointValue(tickLabelStylesMap);
  const maxCharsPerLine = useBreakpointValue(maxCharsPerLineMap) ?? 12;
  const lines = splitStringIntoArrayLines(text as string, maxCharsPerLine);

  // Calculate the height and position of the text
  const lineHeight = 13;
  const totalHeight = lines.length * lineHeight;
  const centerPosition = y + totalHeight / 2 - lineHeight / 2;

  return (
    <Fragment>
      {lines.map((line, index) => {
        const y = centerPosition + index * lineHeight;
        return <VictoryLabel style={style} key={line} x={x} y={y} text={line} textAnchor='middle' />;
      })}
    </Fragment>
  );
};
